import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formsApi } from '@/api/formsApi'
import { cellKey } from '@/lib/tableForm'
import { versionLabel } from '@/lib/versioning'
import {
  describeMappingTarget,
  mappingFieldOptions,
  tableColumnOptions,
  tableFieldById,
  tableRowOptions,
} from '@/lib/mappingTargets'
import { flattenExample } from '@/lib/lis/flattenExample'
import { formsWithMappings, unnamedNestedPaths, type MappedForm } from '@/lib/lis/mappingRules'
import type { LisFieldMapping, LisPreMap, LisSourceSchema } from '@/api/lisConfigApi'
import { lisStageOfTarget, lisTestCodeOfTarget } from '@/lib/lis/fieldStage'
import {
  useCreateLisPreMap,
  useLisPreMaps,
  useLisRecordActions,
  useLisSourceSchemas,
  usePreviewLisPreMap,
  useRemoveLisMapping,
  useRemoveLisPreMap,
  useUpdateLisMapping,
  useUpdateLisPreMap,
} from '@/hooks/useLisConfig'

const PRE_MAPPING_TAB = '?tab=pre-mapping'

/** A field the Source / Value pickers offer: a top-level field or a named path. */
type FieldOption = { path: string; label: string; hint?: string }

/** One row of the table: a mapping (value + condition) and its form field. */
type MappingRow = { preMap: LisPreMap; mapping: LisFieldMapping }

/**
 * Mapping — which laboratory value goes into which form field.
 *
 * The same flow as the EPIC mapping screen: choose a form, then add mappings to
 * it. A mapping reads: when the **Source field** equals the **Expected value**,
 * take the **Value field** and write it to the **Form field**.
 *
 * Source and Value fields are picked from the message's top-level fields and the
 * pre-mappings named on the Pre-Mapping tab — a deep path is named once there
 * rather than re-picked on every mapping. One condition per mapping, by
 * decision: no AND / OR between conditions.
 *
 * Underneath, two mappings with the same Source, Expected and Value fields are
 * one value with two destinations (e.g. a Pre and a Post field). The server
 * groups them.
 *
 * There is no stage here. Which order stage a destination is written for is the
 * FORM FIELD's own setting, made in the form builder on the field, the table
 * column or a cell; this screen only shows it. Fields set to the order's stage
 * win, otherwise those with no stage.
 */
export function LisMappingTab() {
  const schemas = useLisSourceSchemas()
  const preMaps = useLisPreMaps()
  const recordActions = useLisRecordActions().data ?? []
  const [formId, setFormId] = useState('')
  const [editing, setEditing] = useState<MappingRow | null>(null)

  // One source per integration. The FIRST, exactly as the Pre-Mapping tab picks
  // it, so both screens always describe the same source — even for a tenant that
  // still holds extras from before the one-source rule.
  const schema = (schemas.data ?? [])[0] ?? null

  const { data: defsResult } = useQuery({
    queryKey: ['form-definitions', 'for-lis-mapping'],
    queryFn: () => formsApi.listDefinitions(),
  })
  const definitions = defsResult?.items ?? []

  const { data: selectedForm } = useQuery({
    queryKey: ['form-definition', formId],
    enabled: !!formId,
    queryFn: () => formsApi.getDefinition(formId),
  })

  const fields = useMemo(() => fieldOptions(schema), [schema])
  const labelOf = (path: string) => fields.find((f) => f.path === path)?.label ?? path

  // Every form this source writes to, so they can be found without opening
  // each form in turn. Built from the same pre-maps as the table below.
  const mappedForms = useMemo(
    () => (schema ? formsWithMappings(preMaps.data ?? [], schema.id, schema.namedPaths) : []),
    [preMaps.data, schema],
  )
  // The form list above is one page; a mapped form beyond it is fetched by id
  // so the list and the picker can still name it.
  const missingIds = mappedForms
    .map((f) => f.formDefinitionId)
    .filter((id) => !definitions.some((d) => d.id === id))
  const { data: missingDefs } = useQuery({
    queryKey: ['form-definitions', 'for-lis-mapping', 'by-id', missingIds],
    enabled: missingIds.length > 0,
    queryFn: () => Promise.all(missingIds.map((id) => formsApi.getDefinition(id).catch(() => null))),
  })
  const formName = (id: string) => {
    const d = definitions.find((x) => x.id === id) ?? missingDefs?.find((x) => x?.id === id)
    return d ? `${d.name} (${d.versionName ?? versionLabel(d.version)})` : 'Form not found'
  }
  const mappedCount = new Map(mappedForms.map((f) => [f.formDefinitionId, f.destinations]))

  // This form's mappings on this source, one row per destination.
  const rows: MappingRow[] = (preMaps.data ?? [])
    .filter((p) => p.schemaId === schema?.id)
    .flatMap((preMap) =>
      preMap.mappings
        .filter((m) => m.formDefinitionId === formId)
        .map((mapping) => ({ preMap, mapping })),
    )

  if (schemas.isPending) return <p className='text-sm text-muted-foreground'>Loading&hellip;</p>

  if (!schema) {
    return (
      <div className='rounded-xl border bg-white p-6 shadow-sm'>
        <h3 className='text-sm font-medium'>No source yet</h3>
        <p className='mt-2 max-w-prose text-sm text-muted-foreground'>
          Add an example message on the{' '}
          <Link to={PRE_MAPPING_TAB} className='underline'>
            Pre-Mapping
          </Link>{' '}
          tab first. Its fields are what a mapping reads.
        </p>
      </div>
    )
  }

  return (
    <div>
      <p className='mb-4 text-sm text-muted-foreground'>
        When the <strong>Source field</strong> equals the <strong>Expected value</strong>, the{' '}
        <strong>Value field</strong> is written to the <strong>Form field</strong>.
      </p>

      <div className='mb-6 flex flex-wrap items-end gap-4'>
        <div className='grid w-full max-w-md gap-1.5'>
          <Label>Form</Label>
          <SearchableSelect
            value={formId}
            onChange={(id) => {
              setFormId(id)
              setEditing(null)
            }}
            options={definitions.map((d) => {
              const n = mappedCount.get(d.id)
              return {
                value: d.id,
                label: `${d.name} (${d.versionName ?? versionLabel(d.version)})${
                  n ? ` · ${n} mapping${n === 1 ? '' : 's'}` : ''
                }`,
              }
            })}
            selectedOption={formId ? { value: formId, label: formName(formId) } : undefined}
            placeholder='Select a form'
          />
        </div>
      </div>

      {!formId ? (
        <MappedFormsList
          forms={mappedForms}
          loading={preMaps.isPending}
          formName={formName}
          onOpen={(id) => {
            setFormId(id)
            setEditing(null)
          }}
        />
      ) : (
        <>
          <MappingForm
            key={editing ? editing.mapping.id : `new-${formId}-${schema.id}`}
            schema={schema}
            formId={formId}
            selectedForm={selectedForm}
            fields={fields}
            row={editing}
            onDone={() => setEditing(null)}
          />

          {preMaps.isPending ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : rows.length === 0 ? (
            <div className='py-12 text-center text-sm text-muted-foreground'>
              No mappings for this form yet. Add one above.
            </div>
          ) : (
            <div className='overflow-x-persistent rounded-lg border'>
              <table className='w-full text-sm'>
                <thead className='border-b bg-table-header'>
                  <tr className='text-left'>
                    {['Source field', 'Expected value', 'Value field', 'Form field', 'Lab stage', 'If sent again', ''].map(
                      (h) => (
                        <th key={h} className='px-4 py-3 font-medium text-table-header-foreground'>
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <MappingTableRow
                      key={row.mapping.id}
                      row={row}
                      unnamed={unnamedNestedPaths(
                        [...row.preMap.keys.map((k) => k.path), row.preMap.valuePath],
                        schema.namedPaths,
                      )}
                      labelOf={labelOf}
                      selectedForm={selectedForm}
                      actionLabel={
                        recordActions.find((a) => a.value === row.mapping.action)?.label ??
                        row.mapping.action
                      }
                      isEditing={editing?.mapping.id === row.mapping.id}
                      onEdit={() => setEditing(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The fields the Source and Value pickers offer: the message's top-level fields,
 * then the named pre-mappings. A top-level field that also has a name is shown
 * once, by its name.
 */
function fieldOptions(schema: LisSourceSchema | null): FieldOption[] {
  if (!schema) return []
  const named = new Map(schema.namedPaths.map((n) => [n.path, n.name]))
  // The designated fields say what they are, so "match on the test code" is
  // obvious in the Source field list.
  const role = (path: string) =>
    path === schema.testCodePath ? 'test code' : path === schema.orderIdPath ? 'order number' : undefined
  const top = flattenExample(schema.sample)
    .filter((p) => p.topLevel && !named.has(p.path))
    .map((p) => ({ path: p.path, label: p.path, hint: role(p.path) }))
  const names = [...schema.namedPaths]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((n) => ({
      path: n.path,
      label: n.name,
      hint: role(n.path) ? `${n.path} · ${role(n.path)}` : n.path,
    }))
  return [...top, ...names]
}

/**
 * Every form this source writes to — the overview shown before a form is picked,
 * so mapped forms can be checked without opening each form in turn.
 */
function MappedFormsList({
  forms,
  loading,
  formName,
  onOpen,
}: {
  forms: MappedForm[]
  loading: boolean
  formName: (id: string) => string
  onOpen: (formDefinitionId: string) => void
}) {
  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    )
  }
  if (forms.length === 0) {
    return (
      <div className='py-12 text-center text-sm text-muted-foreground'>
        No form has a mapping yet. Select a form to create its first one.
      </div>
    )
  }
  return (
    <div>
      <h3 className='mb-2 text-sm font-semibold'>Forms with mappings</h3>
      <div className='overflow-x-persistent rounded-lg border'>
        <table className='w-full text-sm'>
          <thead className='border-b bg-table-header'>
            <tr className='text-left'>
              {['Form', 'Mappings', 'Expected values', ''].map((h) => (
                <th key={h} className='px-4 py-3 font-medium text-table-header-foreground'>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {forms.map((f) => (
              <tr key={f.formDefinitionId} className='border-b last:border-0'>
                <td className='px-4 py-3 font-medium'>{formName(f.formDefinitionId)}</td>
                <td className='px-4 py-3'>
                  <span className='flex items-center gap-1.5'>
                    {f.destinations}
                    {f.needsPreMapping > 0 ? (
                      <span
                        className='flex items-center gap-1 text-xs text-amber-700'
                        title='These read a nested path directly. Create pre-mappings for the paths, then edit the mappings to pick them.'
                      >
                        <AlertTriangle className='h-3.5 w-3.5' /> {f.needsPreMapping} need a pre-mapping
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className='px-4 py-3 font-mono text-xs text-muted-foreground'>
                  {f.expectedValues.join(', ') || '—'}
                </td>
                <td className='px-4 py-3 text-right'>
                  <Button size='sm' variant='outline' onClick={() => onOpen(f.formDefinitionId)}>
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className='mt-2 text-xs text-muted-foreground'>Or select any form above to add its first mapping.</p>
    </div>
  )
}

function MappingTableRow({
  row,
  unnamed,
  labelOf,
  selectedForm,
  actionLabel,
  isEditing,
  onEdit,
}: {
  row: MappingRow
  /** Nested paths this mapping reads that are not pre-mappings — saved before that was required. */
  unnamed: string[]
  labelOf: (path: string) => string
  selectedForm: Parameters<typeof describeMappingTarget>[0] | undefined
  actionLabel: string
  isEditing: boolean
  onEdit: () => void
}) {
  const removeMapping = useRemoveLisMapping()
  const removePreMap = useRemoveLisPreMap()
  const { preMap, mapping } = row
  const [condition] = preMap.keys
  const target = selectedForm
    ? describeMappingTarget(selectedForm, mapping.targetFieldId).display
    : mapping.targetFieldId

  return (
    <tr className={isEditing ? 'border-b bg-muted/50 last:border-0' : 'border-b last:border-0'}>
      <td className='px-4 py-3'>
        <span className='flex items-center gap-1.5'>
          {unnamed.length > 0 ? (
            <span
              title={`Reads ${unnamed.join(' and ')} directly. Create a pre-mapping for it on the Pre-Mapping tab, then edit this mapping to pick it.`}
            >
              <AlertTriangle className='h-3.5 w-3.5 shrink-0 text-amber-600' aria-label='Needs a pre-mapping' />
            </span>
          ) : null}
          {condition ? labelOf(condition.path) : '—'}
        </span>
      </td>
      <td className='px-4 py-3 font-mono text-xs'>{condition?.expectedValue ?? '—'}</td>
      <td className='px-4 py-3'>{labelOf(preMap.valuePath)}</td>
      <td className='px-4 py-3 font-medium'>{target}</td>
      <td className='px-4 py-3 text-muted-foreground'>
        {lisStageOfTarget(selectedForm, mapping.targetFieldId) ?? 'Any'}
        {lisTestCodeOfTarget(selectedForm, mapping.targetFieldId) ? (
          <span className='ml-1.5 font-mono text-xs' title='Test code, set on the form field in the form builder'>
            · {lisTestCodeOfTarget(selectedForm, mapping.targetFieldId)}
          </span>
        ) : null}
      </td>
      <td className='px-4 py-3 text-muted-foreground'>{actionLabel}</td>
      <td className='px-4 py-3 text-right'>
        <div className='flex items-center justify-end gap-2'>
          <button
            type='button'
            title='Edit this mapping'
            className='text-muted-foreground hover:text-foreground'
            onClick={onEdit}
          >
            <Pencil className='h-4 w-4' />
          </button>
          <button
            type='button'
            title='Remove this mapping'
            className='text-muted-foreground hover:text-destructive'
            onClick={() => {
              // The last destination takes the value with it, so nothing is left
              // reading a field that no mapping writes anywhere.
              if (preMap.mappings.length > 1) removeMapping.mutate(mapping.id)
              else removePreMap.mutate(preMap.id)
            }}
          >
            <Trash2 className='h-4 w-4' />
          </button>
        </div>
      </td>
    </tr>
  )
}

/** "Add a mapping", or the same card editing an existing one in place. */
function MappingForm({
  schema,
  formId,
  selectedForm,
  fields,
  row,
  onDone,
}: {
  schema: LisSourceSchema
  formId: string
  selectedForm: Parameters<typeof mappingFieldOptions>[0] | undefined
  fields: FieldOption[]
  row: MappingRow | null
  onDone: () => void
}) {
  const [condition] = row?.preMap.keys ?? []
  const [sourcePath, setSourcePath] = useState(condition?.path ?? '')
  const [expected, setExpected] = useState(condition?.expectedValue ?? '')
  const [valuePath, setValuePath] = useState(row?.preMap.valuePath ?? '')
  const [fieldId, setFieldId] = useState(row?.mapping.targetFieldId ?? '')
  const [rowId, setRowId] = useState('')
  const [colId, setColId] = useState('')
  const [action, setAction] = useState(row?.mapping.action ?? 'update')

  const create = useCreateLisPreMap()
  const update = useUpdateLisPreMap()
  const updateDestination = useUpdateLisMapping()
  const preview = usePreviewLisPreMap()
  const saving = create.isPending || update.isPending || updateDestination.isPending
  const error = create.error ?? update.error ?? updateDestination.error

  const actionOptions = useLisRecordActions().data ?? []

  // An edited mapping may read a path that is neither top-level nor named any
  // more. It still has to show in the picker, or the Select renders blank.
  const options = useMemo(() => {
    const extra = [condition?.path, row?.preMap.valuePath].filter(
      (p): p is string => !!p && !fields.some((f) => f.path === p),
    )
    return [...fields, ...extra.map((p) => ({ path: p, label: p, hint: 'not a pre-mapping' }))]
  }, [fields, condition?.path, row?.preMap.valuePath])

  const formFields = useMemo(
    () => (selectedForm ? mappingFieldOptions(selectedForm) : []),
    [selectedForm],
  )
  // A table field targets one CELL, so it needs a row and a column as well.
  const tableField = selectedForm && fieldId ? tableFieldById(selectedForm, fieldId) : null
  const target = tableField ? (rowId && colId ? cellKey(rowId, colId) : '') : fieldId

  const keys = sourcePath ? [{ path: sourcePath, expectedValue: expected }] : []
  // Only reachable on a mapping saved before nested paths had to be named: the
  // pickers offer nothing else. The server refuses it too.
  const unnamed = unnamedNestedPaths([sourcePath, valuePath], schema.namedPaths)
  const canSave =
    !!sourcePath &&
    expected.trim() !== '' &&
    !!valuePath &&
    !!target &&
    unnamed.length === 0 &&
    !saving

  // Checked live against the source's example message. A check someone has to
  // ask for is one that gets skipped.
  const hasExample = schema.sample !== null && schema.sample !== undefined
  const previewKey = JSON.stringify({ schemaId: schema.id, valuePath, keys })
  const previewMutate = preview.mutate
  useEffect(() => {
    if (!hasExample || !valuePath || !sourcePath) return
    const timer = setTimeout(() => previewMutate({ schemaId: schema.id, valuePath, keys }), 400)
    return () => clearTimeout(timer)
    // Re-keyed by the serialised input: `keys` is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, hasExample, previewMutate])

  const save = async () => {
    if (!canSave) return
    const destination = {
      formDefinitionId: formId,
      targetFieldId: target,
      action,
    }
    if (row) {
      // Edited IN PLACE: the condition and value belong to the value, the form
      // field to this row. Posting it as new would attach a second destination.
      await update.mutateAsync({ id: row.preMap.id, valuePath, keys })
      await updateDestination.mutateAsync({ id: row.mapping.id, ...destination })
    } else {
      await create.mutateAsync({ schemaId: schema.id, valuePath, keys, destination })
      setExpected('')
      setFieldId('')
      setRowId('')
      setColId('')
      preview.reset()
    }
    onDone()
  }

  return (
    <div className='mb-6 rounded-lg border p-4'>
      <h3 className='mb-3 text-sm font-semibold'>{row ? 'Edit mapping' : 'Add a mapping'}</h3>
      <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        <FieldPicker
          id='lis-source-field'
          label='Source field'
          value={sourcePath}
          options={options}
          onChange={setSourcePath}
        />
        <div className='grid gap-1.5'>
          <Label htmlFor='lis-expected' className='text-xs'>
            Expected value
          </Label>
          <Input
            id='lis-expected'
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            placeholder='e.g. WBCCT'
            className='font-mono'
          />
        </div>
        <FieldPicker
          id='lis-value-field'
          label='Value field'
          value={valuePath}
          options={options}
          onChange={setValuePath}
        />

        <div className='grid gap-1.5'>
          <Label htmlFor='lis-form-field' className='text-xs'>
            Form field
          </Label>
          <Select
            value={fieldId}
            onValueChange={(v) => {
              setFieldId(v)
              setRowId('')
              setColId('')
            }}
          >
            <SelectTrigger id='lis-form-field'>
              <SelectValue placeholder='Field' />
            </SelectTrigger>
            <SelectContent>
              {formFields.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label}
                  {f.isTable ? ' (table)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {tableField ? (
          <>
            <div className='grid gap-1.5'>
              <Label className='text-xs'>Row</Label>
              <Select
                value={rowId}
                onValueChange={(v) => {
                  setRowId(v)
                  // A column on one row may be a spacer on another.
                  setColId('')
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Row' />
                </SelectTrigger>
                <SelectContent>
                  {tableRowOptions(tableField).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='grid gap-1.5'>
              <Label className='text-xs'>Column</Label>
              <Select value={colId} onValueChange={setColId} disabled={!rowId}>
                <SelectTrigger>
                  <SelectValue placeholder={rowId ? 'Column' : 'Pick a row first'} />
                </SelectTrigger>
                <SelectContent>
                  {tableColumnOptions(tableField, rowId).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : null}

        <div className='grid gap-1.5'>
          <Label htmlFor='lis-action' className='text-xs'>
            If this result is sent again
          </Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger id='lis-action'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {actionOptions.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {target ? (
        <p className='mt-3 text-xs text-muted-foreground'>
          Lab stage:{' '}
          <span className='font-medium text-foreground'>
            {lisStageOfTarget(selectedForm, target) ?? 'Any stage'}
          </span>
          , test code:{' '}
          <span className='font-mono font-medium text-foreground'>
            {lisTestCodeOfTarget(selectedForm, target) ?? 'none'}
          </span>
          , both set on the form field in the form builder. The test code is a label only.
        </p>
      ) : null}

      <div className='mt-3 min-h-5 text-xs'>
        {!hasExample || !valuePath || !sourcePath ? null : preview.isPending ? (
          <span className='flex items-center gap-1.5 text-muted-foreground'>
            <Loader2 className='h-3.5 w-3.5 animate-spin' /> Checking against the example&hellip;
          </span>
        ) : preview.data?.matched ? (
          <span className='flex items-center gap-1.5 text-emerald-700'>
            <Check className='h-3.5 w-3.5' /> On the example message this reads{' '}
            <span className='font-mono font-medium'>{String(preview.data.value)}</span>.
          </span>
        ) : preview.data && !preview.data.matched ? (
          <span className='flex items-start gap-1.5 text-amber-700'>
            <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <span>On the example message: {preview.data.detail}</span>
          </span>
        ) : preview.error ? (
          <span className='text-muted-foreground'>
            Couldn&rsquo;t check against the example just now. The mapping can still be saved.
          </span>
        ) : null}
      </div>

      {unnamed.length > 0 ? (
        <p className='mt-2 flex items-start gap-1.5 text-xs text-amber-700'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <span>
            <span className='font-mono'>{unnamed.join(', ')}</span> is nested. Create a pre-mapping for it
            on the{' '}
            <Link to={PRE_MAPPING_TAB} className='underline'>
              Pre-Mapping
            </Link>{' '}
            tab first (give it a name, e.g. <span className='font-mono'>code</span>), then pick that name here.
          </span>
        </p>
      ) : null}

      {error ? <p className='mt-2 text-sm text-destructive'>{error.message}</p> : null}

      <div className='mt-4 flex gap-2'>
        <Button size='sm' onClick={save} disabled={!canSave}>
          {saving ? (
            <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
          ) : row ? null : (
            <Plus className='mr-2 h-3.5 w-3.5' />
          )}
          {row ? 'Save changes' : 'Add mapping'}
        </Button>
        {row ? (
          <Button size='sm' variant='ghost' onClick={onDone}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  )
}

/** A Source or Value field: top-level fields first, then named pre-mappings. */
function FieldPicker({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string
  label: string
  value: string
  options: FieldOption[]
  onChange: (path: string) => void
}) {
  return (
    <div className='grid gap-1.5'>
      <Label htmlFor={id} className='text-xs'>
        {label}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 ? (
            <div className='px-2 py-1.5 text-xs text-muted-foreground'>
              No fields yet — name some on the Pre-Mapping tab.
            </div>
          ) : (
            options.map((o) => (
              <SelectItem key={o.path} value={o.path}>
                {o.label}
                {o.hint ? (
                  <span className='ml-2 font-mono text-[10px] text-muted-foreground'>{o.hint}</span>
                ) : null}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  )
}
